import { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Loading } from '@/components/Loading';
import {
  emailFromAccessToken,
  emailFromSession,
  isStudentAccessDisabled,
} from '@/helpers/studentAccess';
import { useAuth } from '@/stores/auth';
import type { RoleIds } from '@/types/administrator';

interface Config {
  baseUrl: string;
  resellerUrl: string;
  auth: {
    baseUrl: string;
    withCredentials: boolean;
    audience: string;
    applicationUri: string;
  };
  roleIds: RoleIds;
  disabled?: boolean;
  studentsDisabled?: boolean;
  apiKeysDisabled?: boolean;
  bulkAllpayDisabled?: boolean;
}

const defaultConfig: Config = {
  baseUrl: 'https://api.portal.dev.alltix.allbin.se',
  resellerUrl: 'https://api.reseller.dev.allbin.se',
  auth: {
    baseUrl: 'https://api.auth.test.allbin.se',
    audience: 'https://api.trafikportalen.allbin.se',
    withCredentials: true,
    applicationUri: 'https://trafikportalen.allbin.se',
  },
  roleIds: {
    sysAdmin: '8ee99f32-b4c8-4669-a6d7-5da3952b35c0',
    orgAdminWrite: 'a85a0579-a9fd-4db6-86a6-b2e657543b0a',
    orgBearerManager: '412d06ea-3705-4200-90e4-b8ced0d12e92',
    orgAdminRead: '0eb96672-a3df-4af0-82ca-57717cfa729d',
    deptUser: '3f2203d0-6873-4e85-a4e1-09f0dee7e9da',
  },
};

// baseUrl: 'https://api.portal.alltix.allbin.se',
// resellerUrl: 'https://api.reseller.dev.allbin.se',
// auth: {
//   baseUrl: 'https://api.auth.allbin.se',
//   audience: 'https://api.portal.alltix.allbin.se',
//   withCredentials: true,
//   applicationUri: 'https://portal.alltix.allbin.se',
// },

interface ConfigContextValue {
  config: Config;
}

const ConfigContext = createContext<ConfigContextValue | null>(null);

interface ConfigProviderProps {
  children: React.ReactNode;
}

export const ConfigProvider = ({ children }: ConfigProviderProps) => {
  const query = useQuery<Config>({
    queryKey: ['config'],
    queryFn: async () => {
      const response = await axios.get<Partial<Config>>('/config.json');
      return {
        ...defaultConfig,
        ...response.data,
        auth: {
          ...defaultConfig.auth,
          ...response.data.auth,
        },
        roleIds: {
          ...defaultConfig.roleIds,
          ...response.data.roleIds,
        },
      };
    },
  });

  const value = useMemo(
    () => ({
      config: query.data || defaultConfig,
    }),
    [query.data],
  );

  if (query.isLoading) {
    return <Loading />;
  }

  return (
    <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useConfig = (): Config => {
  const context = useContext(ConfigContext);

  if (!context) {
    throw new Error('useConfig must be used within ConfigProvider');
  }

  return context.config;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useRoleIds = (): RoleIds => {
  const config = useConfig();
  return config.roleIds;
};

const useAccountEmail = (): string | undefined =>
  useAuth(
    (state) =>
      emailFromSession(state.session) ??
      state.userProfile?.email ??
      emailFromAccessToken(state.accessToken),
  );

// eslint-disable-next-line react-refresh/only-export-components
export const useStudentsDisabled = (): boolean => {
  const config = useConfig();
  const email = useAccountEmail();

  return isStudentAccessDisabled({
    studentsDisabled: config.studentsDisabled,
    email,
  });
};

// eslint-disable-next-line react-refresh/only-export-components
export const useApiKeysDisabled = (): boolean => {
  const config = useConfig();
  return config.apiKeysDisabled === true;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useBulkAllpayDisabled = (): boolean => {
  const config = useConfig();
  return config.bulkAllpayDisabled === true;
};
